import { useEffect, useRef } from 'react'

const ParticleBackground = () => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    let animationFrameId
    let particles = []

    // Particle class
    class Particle {
      constructor(width, height) {
        this.x = Math.random() * width
        this.y = Math.random() * height
        this.size = Math.random() * 5 + 2
        this.speedX = Math.random() * 1 - 0.5
        this.speedY = Math.random() * 1 - 0.5
        this.opacity = Math.random() * 0.4 + 0.3
      }

      update(canvasWidth, canvasHeight) {
        this.x += this.speedX
        this.y += this.speedY

        if (this.x > canvasWidth) this.x = 0
        if (this.x < 0) this.x = canvasWidth
        if (this.y > canvasHeight) this.y = 0
        if (this.y < 0) this.y = canvasHeight
      }

      draw(ctx) {
        ctx.fillStyle = `rgba(255, 255, 255, ${this.opacity})`
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    // Set canvas size
    const resizeCanvas = () => {
      if (!canvas) return
      const parent = canvas.parentElement
      if (!parent) return
      
      const width = parent.offsetWidth || window.innerWidth
      const height = parent.offsetHeight || window.innerHeight
      
      canvas.width = width
      canvas.height = height
      
      return { width, height }
    }

    // Initialize particles
    const initParticles = (width, height) => {
      particles = []
      const particleCount = 80
      for (let i = 0; i < particleCount; i++) {
        particles.push(new Particle(width, height))
      }
    }

    // Connection lines between nearby particles
    const drawConnections = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x
          const dy = particles[i].y - particles[j].y
          const distance = Math.sqrt(dx * dx + dy * dy)

          if (distance < 150) {
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.3 * (1 - distance / 150)})`
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.moveTo(particles[i].x, particles[i].y)
            ctx.lineTo(particles[j].x, particles[j].y)
            ctx.stroke()
          }
        }
      }
    }

    // Animation loop
    const animate = () => {
      if (!canvas || particles.length === 0) {
        animationFrameId = requestAnimationFrame(animate)
        return
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      particles.forEach((particle) => {
        particle.update(canvas.width, canvas.height)
        particle.draw(ctx)
      })

      drawConnections()

      animationFrameId = requestAnimationFrame(animate)
    }

    // Initialize
    const init = () => {
      const dimensions = resizeCanvas()
      if (dimensions) {
        initParticles(dimensions.width, dimensions.height)
        animate()
      }
    }

    // Wait for DOM to be ready
    const timeoutId = setTimeout(() => {
      init()
    }, 50)

    const handleResize = () => {
      const dimensions = resizeCanvas()
      if (dimensions && particles.length === 0) {
        initParticles(dimensions.width, dimensions.height)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', handleResize)
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
      }
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ pointerEvents: 'none' }}
    />
  )
}

export default ParticleBackground
